import { getFirestore, enableNetwork, disableNetwork, connectFirestoreEmulator, collection, doc, getDoc } from 'firebase/firestore'
import { onAuthStateChanged, User } from 'firebase/auth'
import { auth, db, connectionUtils } from '@/lib/firebase'

export interface ConnectionState {
  isConnected: boolean
  isOffline: boolean
  lastError: Error | null
  retryCount: number
  lastSuccessfulOperation: Date | null
  hasQuicError: boolean
  connectionHealth: 'good' | 'unstable' | 'poor'
}

export class FirebaseConnectionService {
  private static instance: FirebaseConnectionService
  private connectionState: ConnectionState = {
    isConnected: false,
    isOffline: false,
    lastError: null,
    retryCount: 0,
    lastSuccessfulOperation: null,
    hasQuicError: false,
    connectionHealth: 'good'
  }
  private listeners: ((state: ConnectionState) => void)[] = []
  private retryTimeouts: NodeJS.Timeout[] = []
  private maxRetries = 3
  private retryDelay = 2000 // Start with 2 seconds

  private constructor() {
    this.initializeConnectionMonitoring()
  }

  static getInstance(): FirebaseConnectionService {
    if (!FirebaseConnectionService.instance) {
      FirebaseConnectionService.instance = new FirebaseConnectionService()
    }
    return FirebaseConnectionService.instance
  }

  private initializeConnectionMonitoring() {
    // Monitor online/offline status
    window.addEventListener('online', () => {
      console.log('[FirebaseConnection] Browser came online')
      this.updateConnectionState({ isOffline: false })
      this.attemptReconnection()
    })

    window.addEventListener('offline', () => {
      console.log('[FirebaseConnection] Browser went offline')
      this.updateConnectionState({ isOffline: true, isConnected: false })
    })

    // Monitor auth state changes
    onAuthStateChanged(auth, (user: User | null) => {
      if (user) {
        console.log('[FirebaseConnection] User authenticated, testing connection')
        this.testConnection()
      } else {
        console.log('[FirebaseConnection] User signed out')
        this.updateConnectionState({ isConnected: false })
      }
    })

    // Initial connection test
    setTimeout(() => this.testConnection(), 1000)
  }

  private updateConnectionState(updates: Partial<ConnectionState>) {
    this.connectionState = { ...this.connectionState, ...updates }
    console.log('[FirebaseConnection] State updated:', this.connectionState)
    
    // Notify all listeners
    this.listeners.forEach(listener => listener(this.connectionState))
  }

  private isQuicError(error: any): boolean {
    const errorMessage = error?.message || ''
    const errorCode = error?.code || ''

    return errorMessage.includes('ERR_QUIC_PROTOCOL_ERROR') ||
           errorMessage.includes('QUIC') ||
           errorCode.includes('protocol') ||
           errorMessage.includes('net::ERR_QUIC')
  }

  private updateConnectionHealth(error?: any) {
    if (!error) {
      this.connectionState.connectionHealth = 'good'
      return
    }

    if (this.isQuicError(error)) {
      this.connectionState.connectionHealth = 'poor'
      this.connectionState.hasQuicError = true
    } else if (this.connectionState.retryCount > 1) {
      this.connectionState.connectionHealth = 'unstable'
    } else {
      this.connectionState.connectionHealth = 'good'
    }
  }

  private async testConnection(): Promise<boolean> {
    try {
      // Simple test - try to get a reference to a system document using v9+ syntax
      const testRef = doc(db, 'system', 'connection-test')
      await getDoc(testRef)
      
      this.updateConnectionHealth()
      this.updateConnectionState({
        isConnected: true,
        lastError: null,
        retryCount: 0,
        lastSuccessfulOperation: new Date(),
        hasQuicError: false,
        connectionHealth: 'good'
      })

      console.log('[FirebaseConnection] Connection test successful')
      return true
    } catch (error) {
      console.error('[FirebaseConnection] Connection test failed:', error)

      this.updateConnectionHealth(error)
      this.updateConnectionState({
        isConnected: false,
        lastError: error as Error,
        retryCount: this.connectionState.retryCount + 1,
        hasQuicError: this.isQuicError(error),
        connectionHealth: this.connectionState.connectionHealth
      })
      
      return false
    }
  }

  private async attemptReconnection() {
    // Clear existing retry timeouts
    this.retryTimeouts.forEach(timeout => clearTimeout(timeout))
    this.retryTimeouts = []

    if (this.connectionState.retryCount >= this.maxRetries) {
      console.log('[FirebaseConnection] Max retries reached, stopping reconnection attempts')
      return
    }

    const delay = this.retryDelay * Math.pow(2, this.connectionState.retryCount) // Exponential backoff
    console.log(`[FirebaseConnection] Attempting reconnection in ${delay}ms (attempt ${this.connectionState.retryCount + 1}/${this.maxRetries})`)

    const timeout = setTimeout(async () => {
      try {
        await enableNetwork(db)
        const connected = await this.testConnection()
        
        if (!connected && this.connectionState.retryCount < this.maxRetries) {
          this.attemptReconnection()
        }
      } catch (error) {
        console.error('[FirebaseConnection] Reconnection attempt failed:', error)
        if (this.connectionState.retryCount < this.maxRetries) {
          this.attemptReconnection()
        }
      }
    }, delay)

    this.retryTimeouts.push(timeout)
  }

  // Public methods
  public getConnectionState(): ConnectionState {
    return { ...this.connectionState }
  }

  public getConnectionHealthStatus(): {
    health: 'good' | 'unstable' | 'poor'
    hasQuicError: boolean
    recommendation: string
  } {
    const state = this.connectionState

    let recommendation = ''
    if (state.hasQuicError) {
      recommendation = 'QUIC-protokollfel detekterat. Inaktivera QUIC i Chrome (chrome://flags/) eller använd Firefox/Safari.'
    } else if (state.connectionHealth === 'unstable') {
      recommendation = 'Instabil anslutning. Kontrollera din internetanslutning.'
    } else if (state.connectionHealth === 'poor') {
      recommendation = 'Dålig anslutning. Försök att starta om webbläsaren eller byt nätverk.'
    } else {
      recommendation = 'Anslutningen fungerar bra.'
    }

    return {
      health: state.connectionHealth,
      hasQuicError: state.hasQuicError,
      recommendation
    }
  }

  public addListener(listener: (state: ConnectionState) => void) {
    this.listeners.push(listener)
  }

  public removeListener(listener: (state: ConnectionState) => void) {
    const index = this.listeners.indexOf(listener)
    if (index > -1) {
      this.listeners.splice(index, 1)
    }
  }

  public async forceReconnect(): Promise<boolean> {
    console.log('[FirebaseConnection] Force reconnect requested')
    this.updateConnectionState({ retryCount: 0 })

    try {
      // Use the connection utilities for better error handling
      const success = await connectionUtils.resetFirestoreConnection()
      if (success) {
        const connected = await this.testConnection()
        if (connected) {
          console.log('[FirebaseConnection] Force reconnect successful')
        }
        return connected
      }
      return false
    } catch (error) {
      console.error('[FirebaseConnection] Force reconnect failed:', error)
      this.updateConnectionHealth(error)
      this.updateConnectionState({
        lastError: error as Error,
        hasQuicError: this.isQuicError(error),
        connectionHealth: this.connectionState.connectionHealth
      })
      return false
    }
  }

  public getUserFriendlyErrorMessage(error: any): string {
    const errorCode = error?.code || error?.message || 'unknown'
    const errorMessage = error?.message || ''

    console.log('[FirebaseConnection] Translating error:', errorCode, error)

    // QUIC protocol specific errors (check first for more specific handling)
    if (errorMessage.includes('ERR_QUIC_PROTOCOL_ERROR') ||
        errorMessage.includes('QUIC') ||
        errorCode.includes('protocol') ||
        errorMessage.includes('net::ERR_QUIC')) {
      return 'Webbläsarens nätverksprotokoll orsakar anslutningsproblem. Prova att: 1) Inaktivera QUIC i Chrome (chrome://flags/), 2) Rensa webbläsarens cache, eller 3) Använd Firefox/Safari istället.'
    }

    // Network-related errors
    if (errorCode.includes('network') || errorCode.includes('offline')) {
      return 'Nätverksfel. Kontrollera din internetanslutning och försök igen.'
    }

    if (errorCode.includes('unavailable') || errorCode.includes('deadline-exceeded')) {
      return 'Tjänsten är tillfälligt otillgänglig. Försök igen om en stund.'
    }

    if (errorCode.includes('permission-denied')) {
      return 'Åtkomst nekad. Kontrollera att du är inloggad.'
    }

    if (errorCode.includes('not-found')) {
      return 'Den begärda informationen kunde inte hittas.'
    }

    if (errorCode.includes('aborted') || errorCode.includes('cancelled')) {
      return 'Operationen avbröts. Försök igen.'
    }

    // Connection timeout errors
    if (errorMessage.includes('timeout') || errorCode.includes('timeout')) {
      return 'Anslutningen tog för lång tid. Kontrollera din internetanslutning och försök igen.'
    }

    // Generic fallback
    return 'Ett tekniskt fel uppstod. Försök igen eller kontakta support om problemet kvarstår.'
  }

  // Wrapper method for Firestore operations with automatic retry
  public async executeWithRetry<T>(
    operation: () => Promise<T>,
    operationName: string = 'Firestore operation'
  ): Promise<T> {
    const maxAttempts = 3
    let lastError: Error

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        console.log(`[FirebaseConnection] Executing ${operationName} (attempt ${attempt}/${maxAttempts})`)
        const result = await operation()
        
        // Update success state
        this.updateConnectionState({
          isConnected: true,
          lastError: null,
          lastSuccessfulOperation: new Date()
        })
        
        return result
      } catch (error) {
        lastError = error as Error
        console.error(`[FirebaseConnection] ${operationName} failed (attempt ${attempt}/${maxAttempts}):`, error)
        
        this.updateConnectionState({
          isConnected: false,
          lastError: lastError
        })

        // Don't retry on certain errors
        const errorCode = error?.code || ''
        const errorMessage = error?.message || ''

        if (errorCode.includes('permission-denied') ||
            errorCode.includes('not-found') ||
            errorCode.includes('invalid-argument')) {
          throw error
        }

        // For QUIC errors, increase retry delay and reduce attempts
        if (errorMessage.includes('ERR_QUIC_PROTOCOL_ERROR') ||
            errorMessage.includes('QUIC') ||
            errorMessage.includes('net::ERR_QUIC')) {
          console.log(`[FirebaseConnection] QUIC protocol error detected, using extended retry delay`)
        }

        // Wait before retry (except on last attempt)
        if (attempt < maxAttempts) {
          // Use longer delay for QUIC errors
          const isQuicError = errorMessage.includes('ERR_QUIC_PROTOCOL_ERROR') ||
                             errorMessage.includes('QUIC') ||
                             errorMessage.includes('net::ERR_QUIC')

          const baseDelay = isQuicError ? 3000 : 1000 // 3s for QUIC, 1s for others
          const delay = baseDelay * attempt // Linear backoff

          console.log(`[FirebaseConnection] Waiting ${delay}ms before retry (QUIC error: ${isQuicError})`)
          await new Promise(resolve => setTimeout(resolve, delay))
        }
      }
    }

    // All attempts failed
    throw lastError!
  }

  public cleanup() {
    // Clear all timeouts
    this.retryTimeouts.forEach(timeout => clearTimeout(timeout))
    this.retryTimeouts = []
    
    // Clear listeners
    this.listeners = []
  }
}